// ─────────────────────────────────────────────
//  TimelineBreaksCard — who is on shift, and where their four breaks stand.
//
//  One horizontal card per provider, with the four channels: Break, Lunch,
//  Dinner, Bath.
//
//  ⚠️ NO MOCK DATA, DELIBERATELY. The brief asked for local mock rows and
//  toggling. This board is deployed to a live hospital, where a fabricated break
//  would either show a provider resting who is actually in a room, or hide a
//  real request underneath invented state. `useBreakBoardRows` already merges
//  the room doc with the alert bus for exactly this, so the real rows were
//  available and the mock was never needed.
//
//  ⚠️ IT IS READ-ONLY HERE, ALSO DELIBERATELY. Marking a break is a clinical
//  write with a countdown, an alert resolve, and a coverage consequence, and it
//  already has ONE tested home (the room card's break flow, via
//  useRoomBreakActions). Adding a second set of buttons on an at-a-glance panel
//  multiplies the ways a misclick puts a CRNA on a break they never took, for no
//  capability the board does not already have. Each channel is a LINK to the
//  room that owns it: same action, one implementation, no new write path.
//
//  Purely presentational — every value arrives as a prop.
// ─────────────────────────────────────────────
import React from "react";
import { BREAK_CHANNELS, BREAK_STATES, isBeingRelieved, isCoverPending, isCoverCommitted, isBathroom, MEAL_GAP_LONG_MIN, mealNeedLine } from "../../domain/breaks";
import tokens from "../../theme/tokens";

const { color, font, radius } = tokens;

// The four channels, taken from domain/breaks.js rather than retyped.
//
// ⚠️ THE KEYS ARE breakfast / lunch / dinner / BATHROOM. An earlier version of
// this card invented `break` and `bath`, read `row.channels` (the shape is
// `row.breaks`), and looked for a `running` state (it is `in_progress`). Every
// cell therefore resolved to undefined and rendered "none" — a card that looked
// perfectly plausible and was showing nothing real. Sourcing the list from the
// domain is what stops that recurring.
//
// SINGLE LETTERS, and bathroom is WC rather than a second "B". Breakfast and
// bathroom both begin with B, and two identical letters side by side — one
// meaning a meal, one meaning a toilet break — is a misread waiting to happen on
// a dense clinical row. WC is two characters, unmistakable, and standard.
const LETTER = { breakfast: "B", lunch: "L", dinner: "D", bathroom: "WC" };
// Spelled out where a control has to NAME what it is about to record. A ✓ that
// writes "lunch taken" must say the word: "D" is a chip, not a sentence.
const LABEL = { breakfast: "breakfast", lunch: "lunch", dinner: "dinner", bathroom: "bathroom" };

// COLOURS ARE THE BOARD'S OWN, from BREAK_STATES in domain/breaks.js — the same
// source the room card reads, so the two surfaces cannot drift.
//
// They are used as FILL and BORDER, with a darkened ink of the same hue for the
// letter. The raw values are chosen to be legible as chips, not as text: #f59e0b
// as ink on white is 2.15:1 and fails AA outright. Same hue, same meaning,
// readable.
//
// ⚠️ COLOUR IS NOT ALLOWED TO BE THE ONLY DIFFERENCE (WCAG 1.4.1), AND ON THIS
// CARD THAT WAS A CLINICAL PROBLEM, NOT A COMPLIANCE ONE. Every state rendered
// the SAME letter — B, L, D, WC — and differed only by a red / amber / green
// fill, with the state word available solely in a `title` tooltip. Red-amber-
// green is the commonest colour-vision-deficiency confusion triad, and this is
// the card a charge anaesthetist scans to find who is WAITING for a break. A
// reader who cannot separate those hues could not tell "somebody is waiting"
// from "finished" without hovering all four tiles of every row.
//
// `mark` adds a second, non-colour channel: a shape after the letter. Widths
// stay put because the mark is a narrow superscript, not a second glyph slot.
const TINT = {
  none:        { bg: tokens.color.surface.base, ink: "#555e7a", line: tokens.color.border.default, mark: "" },
  requested:   { bg: "#fef2f2", ink: "#991b1b", line: "#ef4444", mark: "!" },
  // ⚠️ THE ONLY DARK TILE ON THE CARD, AND THAT IS THE DESIGN. The other four
  // fills sit within eleven luminance points of each other (0.851–0.960), so in
  // a monochrome photo of the wall, at six feet, or to a colour-blind reader,
  // hue separates none of them. Inverting this one gives the state that most
  // needs noticing — named but NOBODY IN THE ROOM — a channel that survives all
  // three. White on #1e40af is 8.72:1; the tile against the row ground is
  // 7.48:1. The dashed border and the `>` mark are the second and third
  // channels, so it still reads with colour stripped entirely.
  assigned:    { bg: "#1e40af", ink: "#ffffff", line: "#e8edf9", mark: ">" },
  in_progress: { bg: "#fef8ec", ink: "#7a4f00", line: "#b45309", mark: "•" },
  done:        { bg: "#e6f8f3", ink: "#096e53", line: "#0f766e", mark: "✓" },
};

// The board flashes a `requested` bathroom break. The CHANNEL CHIPS here
// deliberately do NOT: a blink steps opacity and hides the very glyph it is
// meant to draw the eye to, and this panel is a summary rather than the surface
// you act on. The colour and the border carry it. (The reliever line below does
// blink, but its RING blinks, not its text — see `s.covering`.)
// ⚠️ THE `none` LABEL IS AN EM DASH, WHICH IS FINE TO LOOK AT AND USELESS TO
// HEAR. Reusing BREAK_STATES.label in the accessible name announced "breakfast
// — — · WC E3"; a screen-reader user learned nothing except that punctuation
// exists. The visible labels stay exactly as they are — this is the spoken
// form, and only the empty state actually needs rewording.
const spokenState = (st, relieverName) => {
  if (st === "none") return "no break recorded";
  // ⚠️ THE SUBJECT CHANGES WITH THE STATE. "On break" describes the person
  // being relieved; "Coming" describes their COVER. Without the name in the
  // accessible name, a screen-reader user hears a state and cannot tell which
  // of two people it is about.
  if (st === "assigned") {
    return relieverName
      ? `cover assigned to ${relieverName}, not in the room yet`
      : "cover assigned, not in the room yet";
  }
  return BREAK_STATES[st].label;
};

const stateOf = (row, ch) => {
  const v = row?.breaks?.[ch];
  return v && BREAK_STATES[v] ? v : "none";
};

// WHO IS STANDING IN THE ROOM while this provider is away.
//
// `relieverName` lives on the ROOM doc and is cleared by `endRoomBreak`, so it
// is only meaningful while a break is actually running. Gate on the break
// STATE rather than on the field being non-empty: a room doc written by any
// other path (a sheet import, a legacy record) could carry a stale name, and a
// board that says a colleague is covering a room they left is worse than one
// that says nothing. Fail closed on the state machine.
//
// Both live write paths (useRoomBreakActions, BreakEnginePanel.handleGiveBreak)
// always pass a name, so the fallback is for old documents only — and it says
// "A reliever", matching RoomCard, rather than inventing a person.
function coveringName(row) {
  if (!isBeingRelieved(row?.breaks || {})) return "";
  return row?.relieverName || "A reliever";
}

// WHO HAS BEEN ASKED AND IS NOT THERE YET.
//
// This line is REQUIRED, not decorative. `coveringName` gates on
// `isBeingRelieved`, which answers `in_progress` only — correctly, since nobody
// is covering — so without a second line the split would make this card LESS
// informative than before: a cover would be named on the room doc and the card
// would show nothing at all about them.
function awaitingName(row) {
  if (!isCoverPending(row?.breaks || {})) return "";
  return row?.relieverName || "A reliever";
}

// WHO CAN BE ASKED TO COVER. The GIVING BREAKS bench, minus anyone who cannot
// actually go: somebody who has left, the person needing the break, and —
// crucially — anybody already committed to another room. `isCoverCommitted`
// rather than `isBeingRelieved`, because a colleague already WALKING to another
// room is not free even though nobody is relieving them; asking the narrow
// question offers the same person to two rooms at once.
function coverCandidates(breakGivers, row) {
  return (Array.isArray(breakGivers) ? breakGivers : []).filter(
    (m) => m && m.id && m.id !== row?.id && !m.departed && !isCoverCommitted(m.breaks || {})
  );
}


function ProviderRow({ row, onOpenRoom, breakGivers = [], onAssignBreak = null, onCancelRequest = null, onClearBreak = null, busy = false }) {
  // Which channel this row is currently picking a cover for. `null` = closed.
  // ⚠️ THE FIRST TAP OPENS THIS AND WRITES NOTHING. A sleeve brushing a chip
  // must never put a colleague on a break; choosing the person is the second
  // tap, and it is the one that writes.
  const [picking, setPicking] = React.useState(null);
  // Whether the ✓ panel is open. Same two-step rule: ✓ opens, the named button
  // writes. Separate state from `picking` so opening one closes the other
  // rather than the row showing two panels at once.
  const [clearing, setClearing] = React.useState(false);
  // One panel at a time: two open at once on a narrow row is two different
  // questions competing for the same answer.
  const openPicker = (ch) => { setClearing(false); setPicking(ch); };
  const canAssign = Boolean(row.roomId && onAssignBreak);
  const candidates = picking ? coverCandidates(breakGivers, row) : [];
  const clickable = Boolean(row.roomId && (onAssignBreak || onOpenRoom));
  const covering = coveringName(row);
  const awaiting = awaitingName(row);
  // "in room since 07:00 · no lunch · 5h20m" — the answer to the question the
  // break CRNA is actually walking around asking. Absent when the board cannot
  // measure it, rather than guessed.
  const gapLine = mealNeedLine(row);
  return (
    <li style={s.person}>
      <div style={s.who}>
        <span style={s.name} title={row.name}>{row.name}</span>
        {row.roomName ? (
          <span style={s.room}>{row.roomName}</span>
        ) : (
          <span style={s.roomUnknown}>no room</span>
        )}
        {gapLine ? (
          <span
            style={row.mealGapMin >= MEAL_GAP_LONG_MIN ? s.gapLong : s.gap}
            data-testid="breaks-gap"
          >
            {gapLine}
          </span>
        ) : null}
        {awaiting ? (
          <span
            style={s.awaiting}
            data-testid="breaks-awaiting"
            title={`${awaiting} has been asked to cover ${row.roomName || "this provider"} and is not in the room yet`}
          >
            ⏳ {awaiting} — not in the room yet
          </span>
        ) : null}
        {covering ? (
          <span
            style={s.covering}
            data-testid="breaks-covering"
            title={`${covering} is covering ${row.roomName || "this provider"} during the break`}
          >
            ☕ {covering} covering
          </span>
        ) : null}
      </div>
      <div style={s.channels}>
        {BREAK_CHANNELS.map((ch) => {
          const st = stateOf(row, ch);
          const t = TINT[st];
          return (
            <button
              key={ch}
              type="button"
              disabled={!clickable || busy}
              onClick={
                !clickable
                  ? undefined
                  : // A channel that can still take a cover opens the picker
                    // right here. One that cannot — already covered, already
                    // done, a cover already on the way — falls back to the room,
                    // where the controls for those states live.
                    //
                    // ⚠️ THE BATHROOM IS THE PROVIDER'S TO ASK FOR. A meal is
                    // scheduled and an AIC assigns it; a WC break is not, and
                    // naming a cover for an untouched bathroom channel is the
                    // board declaring that a colleague needs the toilet. It is
                    // assignable ONLY once that provider has asked (state
                    // `requested`); an untouched WC chip opens the room like any
                    // other unassignable channel. Four identical chips is
                    // exactly how four channels end up behaving identically.
                    canAssign && (st === "requested" || (st === "none" && !isBathroom(ch)))
                      ? () => openPicker(picking === ch ? null : ch)
                      : onOpenRoom
                        ? () => onOpenRoom(row)
                        : undefined
              }
              aria-expanded={
                canAssign && (st === "requested" || (st === "none" && !isBathroom(ch)))
                  ? picking === ch
                  : undefined
              }
              // ⚠️ THE STATE IS IN THE ACCESSIBLE NAME, NOT ONLY THE TOOLTIP.
              // `title` on a button is announced inconsistently and needs a
              // mouse to see at all, so a screen-reader user heard only "B" and
              // learned nothing about whether that break was requested, running
              // or done. The name carries the room too, because these tiles
              // repeat once per provider down the card.
              aria-label={`${ch} — ${spokenState(st, row.relieverName)}${row.roomName ? ` · ${row.roomName}` : ""}`}
              title={`${ch} — ${BREAK_STATES[st].label}${
                canAssign && (st === "requested" || (st === "none" && !isBathroom(ch)))
                  ? " · choose who covers"
                  : clickable ? ` · open ${row.roomName}` : ""
              }`}
              style={{
                ...s.chan,
                background: t.bg,
                color: t.ink,
                borderColor: t.line,
                // The second non-colour channel. Only `assigned` is dashed, so
                // the shortest-fuse state reads even with hue stripped.
                borderStyle: st === "assigned" ? "dashed" : "solid",
                cursor: clickable ? "pointer" : "default",
              }}
              data-channel={ch}
              data-state={st}
            >
              {LETTER[ch] || ch.charAt(0).toUpperCase()}
              {/* The non-colour half of the state. `aria-hidden` because the
                  accessible name above already says the state in words — the
                  mark is for eyes that cannot separate the hues, not for a
                  screen reader, which would otherwise hear "B check". */}
              {t.mark && <span aria-hidden="true" style={s.chanMark}>{t.mark}</span>}
            </button>
          );
        })}
      </div>

      {/* ⚠️ THE ✓ IS HOW A BREAK GETS CLEARED WITHOUT LEAVING THIS CARD. Owner,
          2026-09-11: "we need put a check mark in the right side to help clear
          the breaks." Ending a break meant opening the room, and with fourteen
          people waiting on a meal that is fourteen round trips.

          It does TWO different things and it never guesses which: if somebody is
          actually covering, it ENDS that break and the reliever is recorded; if
          nobody is, it marks a meal taken with no cover — the ordinary case of a
          break that happened off the board. Both write a clinical claim, so the
          panel NAMES the meal and the person confirms it. */}
      {onClearBreak && row.roomId ? (
        <button
          type="button"
          style={s.clearBtn}
          disabled={busy}
          aria-expanded={clearing}
          aria-label={`Clear a break for ${row.name}`}
          title="Clear a break — end one that is running, or mark a meal taken"
          onClick={() => { setPicking(null); setClearing((v) => !v); }}
        >
          ✓
        </button>
      ) : null}

      {clearing && onClearBreak ? (
        <div style={s.picker} role="group" aria-label={`Clear a break for ${row.name}`}>
          <div style={s.pickerHead}>CLEAR A BREAK</div>
          {(() => {
            const running = BREAK_CHANNELS.filter((ch) => stateOf(row, ch) === "in_progress");
            // ⚠️ `assigned` IS EXCLUDED DELIBERATELY, and `completeBreak` throws
            // on it for the same reason: a break nobody ever started cannot be
            // finished, and completing it would record a meal as taken by a
            // provider who never left the room. That one is cancelled, not
            // cleared.
            const markable = BREAK_CHANNELS.filter(
              (ch) => !isBathroom(ch) && stateOf(row, ch) === "none"
            );
            if (!running.length && !markable.length) {
              return (
                <p style={s.pickerEmpty}>
                  Every meal on this row is already recorded, or is waiting on a
                  cover that has not started yet.
                </p>
              );
            }
            const write = async (channel) => {
              const ok = await onClearBreak({ row, channel });
              // Stays open on failure so the toast is read beside the control
              // that produced it, exactly as the cover picker does.
              if (ok !== false) setClearing(false);
            };
            return (
              <ul style={s.pickerList}>
                {running.map((ch) => (
                  <li key={`end-${ch}`}>
                    <button type="button" style={s.pickerOpt} disabled={busy} onClick={() => write(ch)}>
                      {`End ${LABEL[ch] || ch}`}
                      {row.relieverName ? <span style={s.optDoctor}>{row.relieverName} is back</span> : null}
                    </button>
                  </li>
                ))}
                {markable.map((ch) => (
                  <li key={`mark-${ch}`}>
                    <button type="button" style={s.pickerOpt} disabled={busy} onClick={() => write(ch)}>
                      {`Mark ${LABEL[ch] || ch} taken`}
                      {/* SAY WHAT IS NOT BEING RECORDED. Marking a meal taken
                          with nobody named is legitimate — it happened off the
                          board — but the record will not say who covered, and
                          the person pressing this should know that. */}
                      <span style={s.optDoctor}>no cover recorded</span>
                    </button>
                  </li>
                ))}
              </ul>
            );
          })()}
          <button type="button" style={s.pickerCancel} onClick={() => setClearing(false)}>
            Close
          </button>
        </div>
      ) : null}

      {/* ⚠️ INLINE, INSIDE THE ROW — not a popover and not the room modal.
          This panel re-tracks on a ResizeObserver, so a positioned popover
          would drift; and sending an AIC to another page to name a cover is
          what this control exists to stop. `flexBasis: 100%` puts it on its own
          line in the row's flex, exactly as the ORIENTEES actions do, so it
          costs the four-chip strip and the wrapping name column nothing. */}
      {picking && (
        <div style={s.picker} role="group" aria-label={`Who is covering ${picking} for ${row.name}?`}>
          <div style={s.pickerHead}>
            WHO&rsquo;S COVERING {picking.toUpperCase()}?
          </div>
          {candidates.length === 0 ? (
            /* ⚠️ SAY WHY. An empty list with no explanation reads as a broken
               control; this one is a real answer about the department. */
            <p style={s.pickerEmpty}>
              Nobody free to cover right now — no break CRNA and no doctor on
              today&rsquo;s roster. Open the room to assign somebody else.
            </p>
          ) : (
            /* ⚠️ TWO MENUS, ONE PER KIND OF COLLEAGUE. Owner, 2026-09-11: "in
               who is covering, you need to add drop menus for the doctor and
               another one for the providers." It was one flat run of name
               chips, and on a day with no break CRNA flagged it showed four
               anaesthesiologists in a row with nothing saying they were a
               different kind of answer — asking an MD to cover a meal is an
               ordinary but DIFFERENT decision from sending the break CRNA, and
               the two should not be one undifferentiated list to scan.

               ⚠️ STILL TWO DELIBERATE ACTIONS. Opening the menu is one, picking
               a name is the other — the same "a sleeve must never put a
               colleague on a break" rule the chip already follows. The write
               happens on the choice, not on opening. */
            <div style={s.pickerMenus}>
              {[
                ["Doctor", candidates.filter((m) => m.isDoctor), "No doctor free"],
                ["Provider", candidates.filter((m) => !m.isDoctor), "No break CRNA free"],
              ].map(([label, group, emptyWord]) => (
                <label key={label} style={s.pickerLabel}>
                  {label}
                  <select
                    style={s.pickerSelect}
                    // Never bound to a value: this is an ACTION menu, not a
                    // field. Leaving it on the prompt means the next break on
                    // the same row does not open showing the last person asked.
                    value=""
                    disabled={busy || group.length === 0}
                    aria-label={`${label} covering ${picking} for ${row.name}`}
                    onChange={async (e) => {
                      const m = group.find((g) => g.id === e.target.value);
                      if (!m) return;
                      const ok = await onAssignBreak({ row, channel: picking, giver: m });
                      // Stays open on failure so the toast is read next to the
                      // control that produced it, exactly as the room card does.
                      if (ok !== false) setPicking(null);
                    }}
                  >
                    <option value="">
                      {group.length ? `Choose a ${label.toLowerCase()}…` : emptyWord}
                    </option>
                    {group.map((m) => (
                      /* ⚠️ THE ROOM RIDES ALONG. A giver already holding a room
                         has to be pulled out of it before they can walk
                         anywhere, and that is the AIC's decision to make with
                         their eyes open rather than discover afterwards.
                         ⚠️ A STANDING WATCH IS NOT THAT, AND MUST NOT READ AS
                         IT. Owner, 2026-09-13, seeing "Titilope Somade · in
                         WOR 1" on somebody the same board called free: "what do
                         you mean in room 1". Her row was the 07:00 → 07:00
                         no-case watch shape. The watch is still worth saying —
                         it is a call somebody may have to answer mid-break —
                         but it is a different sentence from holding a room. */
                      <option key={m.id} value={m.id}>
                        {m.room
                          ? `${m.name} · in ${m.room}`
                          : m.onCall && m.onCallRoom
                            ? `${m.name} · on call ${m.onCallRoom}`
                            : m.name}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          )}
          {/* ⚠️ A REQUEST MUST BE WITHDRAWABLE. A provider taps "I need a
              break", the bench is empty, and until now the red chip stayed on
              the board with NO WAY BACK — the request outlived the reason for
              it, and the only exit was covering a break that may no longer be
              wanted. It is offered only for a channel actually in `requested`:
              there is nothing to withdraw from a break already under way, and
              cancelling a COVER is a different act with its own control. */}
          {onCancelRequest && stateOf(row, picking) === "requested" ? (
            <button
              type="button"
              style={s.pickerWithdraw}
              disabled={busy}
              onClick={async () => {
                const ok = await onCancelRequest({ row, channel: picking });
                if (ok !== false) setPicking(null);
              }}
            >
              Cancel the {picking} request
            </button>
          ) : null}
          <button type="button" style={s.pickerCancel} onClick={() => setPicking(null)}>
            Close
          </button>
        </div>
      )}
    </li>
  );
}

// WHO IS GIVING THE BREAKS, at the foot of the list of people taking them.
//
// The card answered "who is on a break" and left "who is walking the rounds"
// on a different card entirely (FREE NOW), so the person reading the break
// board had to look somewhere else to find the one colleague the whole list
// depends on. These are bench providers flagged GIVING BREAKS — the SAME flag
// FREE NOW renders, handed in rather than re-derived.
//
// They are NOT provider rows: a break-giver has no four channels of their own
// here (they are the relief, not the relieved), and giving them an identical
// row would invite a reader to look for break state that does not belong to
// them. A footer strip, visually subordinate, under a rule.
function BreakGivers({ people }) {
  // ⚠️ THE FLAG, NOT THE LIST. `breakGivers` answers "who can be ASKED" — which
  // since 2026-09-11 includes every supervisor on the roster, and since
  // 2026-09-13 every free CRNA. This heading asserts something narrower: that
  // these people are walking the break rounds today. Rendering the whole list
  // under it put four anaesthesiologists on a duty nobody assigned them.
  // Owner, 2026-09-13: "the doctors are always availables to gives breaks, dont
  // put them in this list unles s we ask thme to be doing that." The asking is
  // the roster flag, and it is the same flag FREE NOW renders — which is what
  // this strip's own description claimed all along.
  const onRounds = (Array.isArray(people) ? people : []).filter((p) => p && p.givingBreaks);
  if (!onRounds.length) return null;
  const list = onRounds;
  return (
    <div style={s.givers} data-testid="breaks-givers">
      <div style={s.giversHead}>GIVING BREAKS</div>
      <ul style={s.giversList}>
        {list.map((p) => (
          <li key={p.id || p.name} style={s.giver}>
            <span style={s.giverName} title={p.name}>{p.name}</span>
            {/* ⚠️ THE ROOM RIDES IN THE META SLOT, not in a third element. A
                break-giver who is holding a room has to be pulled out of it
                before they can walk the rounds, so the AIC needs to see it —
                but anything ADDED to this one-line row comes out of the only
                shrinkable thing in it, which is the person's name. */}
            {/* ⚠️ `roleLabel`, STAMPED BY `breakGivers`, NEVER GUESSED HERE.
                This read `p.subRole || p.role || "crna"`, and three of the four
                supervisors on the live board carry NEITHER field — so Jessica
                Huang, Gonzalo Martinez and Leigh Tanner, all anaesthesiologists,
                were labelled CRNA. Reported live on 2026-09-11: "Giving brakes,
                all are doctors, no crna." A default that asserts a clinical role
                is a lie waiting for the first person it does not describe, and
                on this row it invites an AIC to ask the wrong colleague. */}
            <span style={s.giverMeta} title={p.room ? `In ${p.room}` : "Free"}>
              {p.roleLabel || ""}
              {p.room ? `${p.roleLabel ? " · " : ""}${p.room}` : ""}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function TimelineBreaksCard({
  rows = [],
  breakGivers = [],
  unavailable,
  onOpenRoom,
  // `({ row, channel, giver }) => Promise<boolean>` — names a cover without
  // leaving this card. Absent (not disabled) on a board that may not write, so
  // a chip then falls back to opening the room, exactly as before.
  onAssignBreak = null,
  // `({ row, channel }) => Promise<boolean>` — withdraws a break REQUEST that
  // nobody has taken. Offered only for a channel still in `requested`: there is
  // nothing to withdraw once somebody is on their way, and calling off a COVER
  // is a different act with its own control.
  onCancelRequest = null,
  // `({ row, channel }) => Promise<boolean>` — the ✓. Ends a break that is
  // RUNNING (the reliever is recorded) or marks a meal taken with no cover, and
  // the panel names which before anything is written. Absent on a board that
  // may not write, so the ✓ simply is not drawn.
  onClearBreak = null,
  busy = false,
}) {
  return (
    <section style={s.card} data-testid="breaks-card">
      <header style={s.cardHead}>
        <h2 style={s.cardTitle}>
          BREAKS
          <span style={s.cardCount}>{unavailable ? "—" : rows.length}</span>
        </h2>
        <span style={s.cardNote}>
          {onAssignBreak ? "tap a break to name a cover" : "open a room to change one"}
        </span>
      </header>

      {unavailable ? (
        <div style={s.unavailable}>
          <strong style={s.unavailableTitle}>Not available for this facility</strong>
          <p style={s.unavailableBody}>
            A break REQUEST lives only in the alert bus, and that follows your
            signed-in campus. Showing these rows here would mix one facility's
            rooms with another's requests.
          </p>
        </div>
      ) : rows.length === 0 ? (
        <p style={s.empty}>Nobody on shift with a break record today.</p>
      ) : (
        <>
          <ul style={s.list}>
            {rows.map((r) => (
              <ProviderRow
                key={r.key || r.id || r.name}
                row={r}
                onOpenRoom={onOpenRoom}
                breakGivers={breakGivers}
                onCancelRequest={onCancelRequest}
                onAssignBreak={onAssignBreak}
                onClearBreak={onClearBreak}
                busy={busy}
              />
            ))}
          </ul>
          <BreakGivers people={breakGivers} />
        </>
      )}
    </section>
  );
}

const s = {
  card: {
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.lg,
    padding: "11px 13px 13px",
    minWidth: 0,
    boxShadow: tokens.shadow.sm,
  },
  cardHead: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 9,
    paddingBottom: 7,
    borderBottom: `1px solid ${color.border.default}`,
  },
  cardTitle: {
    margin: 0,
    fontSize: 11,
    fontWeight: font.weight.bold,
    letterSpacing: ".9px",
    color: color.text.secondary,
    display: "flex",
    alignItems: "center",
    gap: 7,
  },
  cardCount: {
    fontSize: 10,
    fontWeight: font.weight.bold,
    background: color.surface.raised,
    color: color.text.secondary,
    borderRadius: radius.full,
    padding: "1px 7px",
    letterSpacing: 0,
  },
  cardNote: { fontSize: 10.5, color: color.text.muted, whiteSpace: "nowrap" },

  // TWO-UP WHEN THERE IS ROOM, ONE-UP WHEN THERE IS NOT — and this is a layout
  // fix, not a cosmetic one. This card carries one row per provider on the
  // board (11 on the day it was measured, 16 on a full one), which made it
  // 491px tall and therefore the tallest thing in the side panel. Every card
  // beside it was stretched to match and sat 77-83% empty. Reflowing the rows
  // into as many columns as fit halves the height at the source.
  // `auto-fill`, not a fixed count: these rows are uniform and interchangeable,
  // so "as many as fit" is genuinely the right question here (it is the WRONG
  // question for a panel whose rows carry meaning — see TimelineSidePanels).
  list: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: 3,
  },
  person: {
    display: "flex",
    // ⚠️ WRAP IS LOAD-BEARING FOR THE PICKER. `flexBasis: 100%` only reaches its
    // own line if the line can break; without this the picker would be squeezed
    // into the row beside the chips and take its width out of the name — the
    // one thing this card's layout is built to protect. The row's two existing
    // children cannot trigger a wrap on their own: `who` is `flex: 1` with
    // `minWidth: 0`, so the name shrinks instead of pushing.
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
    padding: "4px 7px",
    background: color.surface.base,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.sm,
    minWidth: 0,
  },
  who: { display: "flex", flexDirection: "column", minWidth: 0, flex: 1 },
  // ⚠️ WEIGHT, NOT COLOUR, CARRIES "THIS ONE FIRST". Nobody is in danger here —
  // somebody is hungry — and spending the board's red or amber on it is how
  // those colours stop meaning anything on the surface that owns real alarms.
  gap: {
    fontSize: 9.5,
    color: color.text.muted,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  gapLong: {
    fontSize: 9.5,
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  // Its own line in the row's flex — see the note at the render site.
  picker: {
    flexBasis: "100%",
    marginTop: 6,
    padding: "6px 7px",
    background: color.surface.card,
    border: `1px solid ${color.accent.primary}`,
    borderRadius: radius.sm,
    minWidth: 0,
  },
  pickerHead: {
    fontSize: 9.5,
    fontWeight: 800,
    letterSpacing: ".4px",
    color: "#1e3a8a",
    marginBottom: 5,
  },
  pickerList: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexWrap: "wrap", gap: 5 },
  // ⚠️ 44px, NOT THE 24px OF THE CHIPS. These rows WRITE — they put a named
  // colleague on their way to a room — and a write must not sit on a target
  // sized for an at-a-glance indicator.
  pickerOpt: {
    minHeight: 44,
    padding: "0 12px",
    background: color.surface.base,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.sm,
    fontSize: 11.5,
    fontWeight: 700,
    color: color.text.primary,
    cursor: "pointer",
  },
  pickerCancel: {
    marginTop: 5,
    minHeight: 44,
    padding: "0 12px",
    background: "transparent",
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.sm,
    fontSize: 11,
    color: color.text.secondary,
    cursor: "pointer",
  },
  // ⚠️ AMBER, NEVER THE ALARM RED. Withdrawing a request is an ordinary,
  // correct act — the reason for it passed — and this card's red already means
  // "a break is waiting for cover". Spending it here would blunt the one colour
  // that has to keep meaning somebody is stuck.
  pickerWithdraw: {
    marginTop: 5,
    marginRight: 5,
    minHeight: 44,
    padding: "0 12px",
    background: "transparent",
    border: `1px solid ${color.status.closing.border}`,
    borderRadius: radius.sm,
    fontSize: 11,
    fontWeight: font.weight.bold,
    color: color.status.closing.text,
    cursor: "pointer",
  },
  // The MD tag on a candidate. Small, and never the only signal — the name is
  // still the name; this says which kind of colleague is being asked.
  optDoctor: {
    marginLeft: 5,
    fontSize: 9,
    fontWeight: font.weight.bold,
    letterSpacing: ".4px",
    color: color.text.muted,
  },
  pickerEmpty: { margin: "0 0 5px", fontSize: 11, color: color.text.muted },
  // ⚠️ ON THE RIGHT, AND IT DOES NOT TAKE THE NAME'S WIDTH. `marginLeft:auto`
  // pushes it to the row's edge; `flexShrink: 0` keeps it whole. The name is the
  // only part of this row that can give, so anything added to the line comes out
  // of it — the bug that cut "Stephanie Manganello" when the chips grew 4px.
  clearBtn: {
    marginLeft: "auto",
    flexShrink: 0,
    minWidth: 32,
    minHeight: 24,
    padding: "0 8px",
    background: "transparent",
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.sm,
    fontSize: 12,
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    cursor: "pointer",
  },
  // The two menus sit side by side and WRAP rather than shrinking: a select
  // squeezed below its content truncates the names inside it, and a name is the
  // whole point of this control.
  pickerMenus: { display: "flex", flexWrap: "wrap", gap: 8 },
  pickerLabel: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    fontSize: 9.5,
    fontWeight: font.weight.bold,
    letterSpacing: ".4px",
    textTransform: "uppercase",
    color: color.text.muted,
    minWidth: 0,
  },
  // ⚠️ 44px, for the same reason `pickerOpt` is: choosing here WRITES — it puts
  // a named colleague on their way to a room.
  pickerSelect: {
    minHeight: 44,
    // ⚠️ 16px, NEVER SMALLER. iOS zooms the whole page in on a control with a
    // font under 16px and never zooms back out, which is the "the page moves
    // side to side" report.
    fontSize: 16,
    padding: "0 8px",
    maxWidth: 240,
    background: color.surface.base,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.sm,
    fontWeight: font.weight.bold,
    color: color.text.primary,
    cursor: "pointer",
  },
  // ⚠️ WRAPS, NEVER TRUNCATES. This is the same bug the FREE NOW rows had: the
  // row is a flex line and the name is the only part of it that can give, so it
  // is the part that loses. Widening the channel tiles by 4px each to carry the
  // state mark was enough to cut "Stephanie Manganello" at 900px. A break board
  // that cannot say WHO is on a break is not a break board, so a long name takes
  // a second line rather than an ellipsis.
  name: {
    fontSize: 12,
    fontWeight: font.weight.bold,
    color: color.text.primary,
    minWidth: 0,
    overflowWrap: "anywhere",
    lineHeight: 1.2,
  },
  room: { fontSize: 10, color: color.text.muted },
  roomUnknown: { fontSize: 10, color: color.text.muted, fontStyle: "italic" },

  // ⚠️ THE RING BLINKS, NOT THE NAME. `atcFlash` fades the whole element to 25%
  // opacity, which would hide the reliever's name for half of every second —
  // the one word this line exists to deliver. `atcRing` animates `outline-color`
  // only, so the attention-grab survives and the text never dims
  // (index.js:42-50). Under prefers-reduced-motion the global rule kills the
  // animation and the amber pill remains, so the line is still unmistakable
  // with no motion at all.
  //
  // `alignSelf: "flex-start"` is load-bearing: `who` is a flex COLUMN, so this
  // is the cross axis and the pill hugs its text instead of stretching the full
  // column width. Long names ellipsis with the whole name in the tooltip.
  // ⚠️ NAVY, AND IT DOES NOT RING. Amber means "a reliever is standing in the
  // room" on every ATC surface; borrowing it here would say the opposite of the
  // truth. And `atcRing` is the board's attention animation — an answered ask
  // that blinks exactly like an unanswered one erases the answer. Escalation for
  // a cover that never turns up is owned by NEEDS YOU NOW, which owns alarm
  // vocabulary; this line's whole job is to be readable and calm.
  awaiting: {
    alignSelf: "flex-start",
    maxWidth: "100%",
    marginTop: 2,
    fontSize: 10.5,
    fontWeight: font.weight.bold,
    letterSpacing: ".2px",
    color: "#1e3a8a",
    background: "#e8edf9",
    borderRadius: radius.sm,
    padding: "1px 5px",
    outline: "1.5px solid #1e40af",
    outlineOffset: -1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  covering: {
    alignSelf: "flex-start",
    maxWidth: "100%",
    marginTop: 2,
    fontSize: 10.5,
    fontWeight: font.weight.bold,
    letterSpacing: ".2px",
    color: "#7a4f00",
    background: "#fef8ec",
    borderRadius: radius.sm,
    padding: "1px 5px",
    outline: "1.5px solid #f59e0b",
    outlineOffset: -1,
    animation: "atcRing 1s steps(1) infinite",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  channels: { display: "flex", gap: 3, flexShrink: 0 },
  // 11px is fine on a BUTTON (the 16px floor is about focusable TEXT INPUTS,
  // which iOS zooms into; a button does not take a caret).
  // Condensed: a fixed square per channel so four of them read as one strip and
  // every provider row lines up vertically down the card.
  chan: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 800,
    fontFamily: "inherit",
    letterSpacing: ".3px",
    padding: 0,
    width: 28,
    height: 24,
    borderRadius: radius.sm,
    border: "1px solid",
  },
  // The state mark, riding high and small beside the channel letter. 2px of
  // extra button width pays for it (26 -> 28) rather than shrinking the letter;
  // it was 30, which cost a name at 900px (see `name` above).
  chanMark: { fontSize: 8, lineHeight: 1, marginLeft: 0.5, alignSelf: "flex-start", marginTop: 4 },

  // Subordinate to the list, and separated by a rule rather than a gap: these
  // people are the ANSWER to the list above, not more of it. Break amber, the
  // same hue as the GIVING BREAKS chip on FREE NOW, so the two surfaces read as
  // one fact.
  givers: {
    marginTop: 8,
    paddingTop: 7,
    borderTop: `1px solid ${color.border.default}`,
  },
  giversHead: {
    fontSize: 10.5,
    fontWeight: font.weight.bold,
    letterSpacing: ".8px",
    color: "#7a4f00",
    marginBottom: 5,
  },
  giversList: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexWrap: "wrap", gap: 4 },
  giver: {
    display: "flex",
    alignItems: "baseline",
    gap: 5,
    background: "#fef8ec",
    border: "1px solid #f59e0b",
    borderRadius: radius.sm,
    padding: "3px 7px",
    minWidth: 0,
  },
  giverName: {
    fontSize: 11,
    fontWeight: font.weight.bold,
    color: "#7a4f00",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  giverMeta: { fontSize: 10.5, letterSpacing: ".3px", color: "#7a4f00", opacity: 0.8 },

  empty: { margin: 0, fontSize: 11, color: color.text.muted, fontStyle: "italic" },
  unavailable: {
    background: "#fef8ec",
    border: `1px solid ${color.accent.closing}`,
    borderRadius: radius.sm,
    padding: "10px 11px",
  },
  unavailableTitle: { fontSize: 12, color: "#7a4f00", display: "block" },
  unavailableBody: { fontSize: 10.5, color: "#7a4f00", lineHeight: 1.45, margin: "4px 0 0" },
};
